import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography, TextField, Button, Divider, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { GoogleIcon, FacebookIcon } from 'icons/brandIcons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../contexts/UserContext';
import { getUserById, forgotPassword, checkUserExists, resendVerificationEmail } from 'apis/authApi';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { VisibilityIcon, VisibilityOffIcon } from 'icons/materialIcons';
import { validateEmail } from 'utils/validator';
import { getLoginRedirectPath } from '../utils/authUtils';

export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const navigate = useNavigate();
  const { login, googleSignIn, isAuthenticated, facebookSignIn, authState } = useAuth();
  const { setUserId, setUserName, setUserEmail } = useUserContext();
  const emailInputRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'warning'
  });

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleEmailChange = (event) => {
    const inputEmail = event.target.value;
    setEmail(inputEmail);
    setEmailError('');
    setError('');
    setSuccessMessage('');

    if (!validateEmail(inputEmail)) {
      setEmailError('Invalid email address!');
    }
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setPasswordError('');
    setError('');
    setSuccessMessage('');
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPasswordClick = () => {
    setIsForgotPasswordMode(true);
    setError('');
    setEmailError('');
    setPasswordError('');
    setPassword('');
    setSnackbar({ open: false, message: '', severity: 'warning' });
  };

  const handleBackToSignIn = () => {
    setIsForgotPasswordMode(false);
    setError('');
    setEmailError('');
    setPasswordError('');
    setSnackbar({ open: false, message: '', severity: 'warning' });
  };

  const handleSubmitForgotPassword = async () => {
    if (!email) {
      setEmailError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Invalid email address');
      return;
    }

    try {
      setLoading(true);

      const exists = await checkUserExists(email);
      if (!exists) {
        setEmailError('Email does not exist');
        return;
      }

      const response = await forgotPassword(email);

      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Password reset email sent. Please check your inbox.',
          severity: 'success'
        });
        setTimeout(() => {
          handleBackToSignIn();
        }, 2000);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  // UPDATED: Enhanced cleanup for signup flags
  useEffect(() => {
    // Clear ALL signup-related flags when user visits sign-in page
    localStorage.removeItem('signupInProgress');
    localStorage.removeItem('currentPage');
    localStorage.removeItem('contactLinkUUID');
    localStorage.removeItem('tempLinksFetched');
    localStorage.removeItem('userState'); // Clear during sign-in visit
    localStorage.removeItem('cardState'); // Clear during sign-in visit

    // Check if user just registered
    const initialSignup = localStorage.getItem('initialSignup');
    const verificationEmail = localStorage.getItem('verificationEmail');

    if (initialSignup === 'true' && verificationEmail) {
      // Just registered - only set email, don't show verification messages yet
      setEmail(verificationEmail);
      localStorage.removeItem('initialSignup');
    }
  }, []);

  useEffect(() => {
    // Additional cleanup - ensure complete reset
    localStorage.removeItem('signupInProgress');
    localStorage.removeItem('currentPage');
    localStorage.removeItem('contactLinkUUID');
    localStorage.removeItem('tempLinksFetched');

    if (isAuthenticated()) {
      // Determine redirect path based on user plan and role
      const determineRedirect = async () => {
        if (authState?.user && authState?.token) {
          try {
            const redirectPath = await getLoginRedirectPath(authState.user, authState.token);
            navigate(redirectPath);
          } catch (error) {
            console.error('Error determining redirect path:', error);
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      };
      determineRedirect();
    } else {
      emailInputRef.current?.focus();
    }
  }, [isAuthenticated, navigate, authState]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    const response = await googleSignIn();

    if (response.success) {
      // Determine redirect path based on user plan and role
      try {
        const user = response.user || response.firebaseUser;
        const idToken = response.idToken;
        if (user && idToken) {
          const redirectPath = await getLoginRedirectPath(user, idToken);
          navigate(redirectPath);
        } else {
          // Fallback: wait for authState to update, then redirect
          setTimeout(() => {
            if (authState?.user && authState?.token) {
              getLoginRedirectPath(authState.user, authState.token).then(path => navigate(path));
            } else {
              navigate('/dashboard');
            }
          }, 500);
        }
      } catch (error) {
        console.error('Error determining redirect path:', error);
        navigate('/dashboard');
      }
    } else {
      setSnackbar({
        open: true,
        message: 'Please register first to create your account',
        severity: 'warning'
      });

    }
    setLoading(false);
  };

  const handleFacebookSignIn = async () => {
    setLoading(true);
    try {
      const response = await facebookSignIn();
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (response.success) {
        // Determine redirect path based on user plan and role
        try {
          const user = response.user;
          const idToken = response.idToken;
          if (user && idToken) {
            const redirectPath = await getLoginRedirectPath(user, idToken);
            navigate(redirectPath);
          } else {
            // Fallback: wait for authState to update, then redirect
            setTimeout(() => {
              if (authState?.user && authState?.token) {
                getLoginRedirectPath(authState.user, authState.token).then(path => navigate(path));
              } else {
                navigate('/dashboard');
              }
            }, 500);
          }
        } catch (error) {
          console.error('Error determining redirect path:', error);
          navigate('/dashboard');
        }
      } else {
        setError('Failed to sign in with Facebook');
      }
    } catch (error) {
      console.error('Error during Facebook sign-in:', error);
      setError('An error occurred during Facebook sign-in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (event) => {
    event.preventDefault();

    if (!email) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Invalid email address!');
      return;
    }
    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    setLoading(true);
    try {

      const userExists = await checkUserExists(email);
      if (!userExists) {
        setSnackbar({
          open: true,
          message: 'Please register first to create your account',
          severity: 'warning'
        });
        setLoading(false);
        return;
      }

      const response = await login(email, password);

      if (response.success) {
        const { user, idToken } = response;
        console.log("user after signin is " + user.userEmail);
        console.log("Response is " + response);
        setUserId(user.userId);
        try {
          const useData = await getUserById(user.userId, idToken);
          setUserName(useData.data.fullName);
          console.log('Fetched user data from login:', useData.data.fullName);
          setUserEmail(useData.data.email);
        } catch (error) {
          console.error('Error fetching user data after login:', error);
          // Continue with navigation even if getUserById fails
          // AuthContext will handle fetching user data via onAuthStateChanged
        }
        
        // Determine redirect path based on user plan and role
        const redirectPath = await getLoginRedirectPath(user, idToken);
        navigate(redirectPath);
      } else if (response.code === 'auth/email-not-verified') {
        setSnackbar({
          open: true,
          message: 'Email not verified. Please verify your email before signing in.',
          severity: 'error'
        });
        setError('Please verify your email to sign in.');

        // Now we show the verification options
        setShowResendVerification(true);

        // Check for existing cooldown only when sign-in fails due to unverified email
        const cooldownEndTime = localStorage.getItem('resendCooldownEnd');
        if (cooldownEndTime) {
          const remainingTime = parseInt(cooldownEndTime) - Date.now();
          if (remainingTime > 0) {
            setResendCooldown(true);
            setTimeout(() => {
              setResendCooldown(false);
              localStorage.removeItem('resendCooldownEnd');
            }, remainingTime);
          }
        }
      } else if (response.code === 'auth/invalid-credential') {
        setSnackbar({
          open: true,
          message: 'Invalid email or password. Please try again.',
          severity: 'error'
        });
        setError('Invalid email or password');
      } else {
        setError('Failed to sign in with email');
      }
    } catch (error) {
      if (error.code === 'auth/email-not-verified') {
        setSnackbar({
          open: true,
          message: 'Email not verified. Please verify your email before signing in.',
          severity: 'error'
        });
        setError('Please verify your email to sign in.');
        setShowResendVerification(true); // Always set to true when email is not verified
      } else if (error.code === 'auth/invalid-credential') {
        setSnackbar({
          open: true,
          message: 'Invalid email or password. Please try again.',
          severity: 'error'
        });
        setError('Invalid email or password');
      } else {
        setError(error.message || 'An error occurred during email sign-in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Also modify the handleResendVerification function to store the email:
  const handleResendVerification = async () => {
    try {
      setLoading(true);
      const response = await resendVerificationEmail(email);

      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Verification email resent. Please check your inbox.',
          severity: 'success'
        });
        const cooldownEndTime = Date.now() + 60000;
        localStorage.setItem('resendCooldownEnd', cooldownEndTime.toString());
        localStorage.setItem('verificationEmail', email); // Store the email
        setResendCooldown(true);
        setTimeout(() => {
          setResendCooldown(false);
          localStorage.removeItem('resendCooldownEnd');
          localStorage.removeItem('verificationEmail');
        }, 60000);
      } else {
        setError('Failed to resend verification email');
      }
    } catch (error) {
      setError('Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (isForgotPasswordMode) {
        handleSubmitForgotPassword();
      } else {
        handleEmailSignIn(event);
      }
    }
  };

  const renderForgotPasswordMode = () => (
    <>
      <Typography variant="h5" gutterBottom>
        Forgot Password
      </Typography>
      <Typography variant="body2" sx={{ marginBottom: 3, textAlign: 'center' }}>
        Enter your email address and we'll send you instructions to reset your password.
      </Typography>

      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={handleEmailChange}
        onKeyDown={handleKeyDown}
        variant="outlined"
        fullWidth
        sx={{ marginBottom: 2 }}
        error={!!emailError}
        helperText={emailError}
        disabled={loading}
        autoFocus
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmitForgotPassword}
        disabled={loading}
        sx={{
          marginBottom: 2,
          backgroundColor: 'blue',
          borderRadius: '4px',
          textTransform: 'none'
        }}
      >
        {loading ? <CircularProgress size={24} /> : 'Reset Password'}
      </Button>

      <Button
        fullWidth
        variant="text"
        onClick={handleBackToSignIn}
        sx={{ textTransform: 'none' }}
      >
        Back to Sign In
      </Button>

      {error && (
        <Typography color="error" sx={{ marginTop: 2, textAlign: 'center' }}>
          {error}
        </Typography>
      )}
    </>
  );

  return (
    <Box sx={{ flexGrow: 1, height: '100vh' }}>
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={handleSnackbarClose}
      />
      <Grid container columns={16} sx={{ height: '100%' }}>
        <Grid
          item
          xs={12}
          sm={7}
          md={7}
          sx={{
            backgroundColor: 'white',
            height: '100%',
            borderTopRightRadius: '26px',
            borderBottomRightRadius: '26px',
            padding: '54px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxSizing: 'border-box',
            width: '100%',
            flex: '0 0 50%'
          }}
        >
          {isForgotPasswordMode ? (
            renderForgotPasswordMode()
          ) : (
            <>
              <Typography variant="h5" gutterBottom>
                Sign In
              </Typography>
              <Typography variant="body2" sx={{ marginBottom: 2, textAlign: 'center' }}>
                Welcome back to InTouchWe! Please sign in to access your digital business card.
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleSignIn}
                startIcon={<GoogleIcon />}
                sx={{
                  marginBottom: 1,
                  marginTop: 4,
                  textTransform: 'none',
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign in with Google'}
              </Button>

              <Divider sx={{ width: '100%', marginBottom: 2 }} />

              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                onKeyDown={handleKeyDown}
                inputRef={emailInputRef}
                variant="outlined"
                fullWidth
                sx={{ marginBottom: 1 }}
                error={!!emailError}
                helperText={emailError}
                disabled={loading}
              />

              <Box sx={{ width: '100%', position: 'relative' }}>
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyDown={handleKeyDown}
                  variant="outlined"
                  error={!!passwordError}
                  helperText={passwordError}
                  fullWidth
                  sx={{ marginBottom: 1 }}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    cursor: 'pointer',
                    textAlign: 'right',
                    mb: 2,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                  onClick={handleForgotPasswordClick}
                >
                  Forgot password?
                </Typography>
              </Box>

              <Box sx={{
                width: '100%',
                display: 'flex',
                gap: 2,
                marginBottom: 2
              }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    flex: 1,
                    backgroundColor: 'blue',
                    borderRadius: '4px',
                    textTransform: 'none'
                  }}
                  onClick={handleEmailSignIn}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    flex: 1,
                    backgroundColor: 'blue',
                    borderRadius: '4px',
                    textTransform: 'none'
                  }}
                  onClick={() => navigate('/sign-up')}
                  disabled={loading}
                >
                  Create Account
                </Button>
              </Box>

              {showResendVerification && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Please check your inbox and spam folder for the verification email.
                  </Typography>
                  <Button
                    variant="text"
                    onClick={handleResendVerification}
                    disabled={loading || resendCooldown}
                    sx={{
                      textTransform: 'none',
                      color: 'primary.main',
                      '&.Mui-disabled': {
                        color: 'text.disabled',
                        opacity: 0.6
                      }
                    }}
                  >
                    {resendCooldown
                      ? 'Please wait 1 minute to resend verification email'
                      : 'Resend Verification Email'}
                  </Button>
                </Box>
              )}

            </>
          )}
        </Grid>

        <Grid
          item
          xs={12}
          sm={9}
          md={9}
          sx={{
            backgroundColor: '#f1f1f1',
            height: '100%',
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography variant="h4">
            Welcome back to InTouchWe!
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}